import { useEffect, useMemo, useState } from "react";
import { APP_VERSION } from "../version";
import {
  isNewSinceLastOpen,
  notesNewestFirst,
  readSeenReleaseVersion,
  writeSeenReleaseVersion,
  type CompanionReleaseNote,
} from "../releaseNotes";

function notesStorage(): Storage | null {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

export function ReleaseNotes({
  storage,
  notes,
}: {
  storage?: Storage | null;
  notes?: readonly CompanionReleaseNote[];
}) {
  const store = storage !== undefined ? storage : notesStorage();
  const entries = useMemo(() => notesNewestFirst(notes), [notes]);
  const [seenVersion, setSeenVersion] = useState<string | null>(() => readSeenReleaseVersion(store));

  useEffect(() => {
    writeSeenReleaseVersion(APP_VERSION, store);
    setSeenVersion((prev) => prev ?? null);
  }, [store]);

  return (
    <section className="panel bevel-out release-notes" aria-labelledby="release-notes-title" data-testid="release-notes">
      <p className="kicker" id="release-notes-title">
        Release Notes
      </p>
      <p className="mono" data-testid="release-notes-current">
        Version {APP_VERSION}
      </p>
      <p className="muted">What’s on this Northstar Dispatch Mobile build. Independent of the desk number.</p>
      <p className="field-hint">
        PATCH on every mobile ship. MINOR when Emerson asks. Same number as the header.
      </p>
      <ol className="release-notes-list">
        {entries.map((entry) => {
          const isCurrent = entry.version === APP_VERSION;
          const isNew = isNewSinceLastOpen(entry.version, seenVersion, APP_VERSION);
          return (
            <li
              key={entry.version}
              className={`release-notes-entry${isCurrent ? " is-current" : ""}`}
              data-testid={`release-notes-${entry.version}`}
              data-current={isCurrent ? "true" : "false"}
              data-new={isNew ? "true" : "false"}
            >
              <div className="release-notes-head">
                <span className="mono">v{entry.version}</span>
                {isCurrent ? <span className="release-notes-pill">This phone</span> : null}
                {isNew ? <span className="release-notes-pill is-new">New</span> : null}
              </div>
              <p className="release-notes-summary">{entry.summary}</p>
              <ul>
                {entry.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
